import { FC, FocusEventHandler, memo, useMemo } from 'react'
import { CompanyType, PlanningClassification } from '@leadgen/models/client'
import Box from '@mui/material/Box'
import ClassificationGroup from './ClassificationGroup'
import {
  planningClassificationGroups,
  planningClassificationsByCompanyTypeMap,
} from '../../../../constants/planningTaxonomy'
import useStableCallback from '../../../../hooks/shared/useStableCallback'

const ClassificationGrid: FC<{
  companyType: CompanyType
  planningClassifications: PlanningClassification[]
  onChange: (planningClassifications: PlanningClassification[]) => void
  onBlur?: FocusEventHandler<HTMLButtonElement>
}> = ({ planningClassifications, companyType, onChange, onBlur }) => {
  const selectedPlanningClassifications = useMemo(
    () => new Set(planningClassifications),
    [planningClassifications],
  )
  const allowedPlanningClassifications = useMemo(
    () => new Set(planningClassificationsByCompanyTypeMap[companyType] || []),
    [companyType],
  )
  const groups = useMemo(
    () =>
      planningClassificationGroups
        .map(group => ({
          ...group,
          options: group.options.filter(option => allowedPlanningClassifications.has(option)),
        }))
        .filter(group => group.options.length),
    [allowedPlanningClassifications],
  )
  const onClassificationChange = useStableCallback(
    (planningClassification: PlanningClassification, checked: boolean) => {
      if (checked) {
        onChange([...planningClassifications, planningClassification])
        return
      }
      onChange(
        planningClassifications.filter(
          selectedPlanningClassification =>
            selectedPlanningClassification !== planningClassification,
        ),
      )
    },
  )
  const onSelectAllChange = useStableCallback(
    (groupPlanningClassifications: PlanningClassification[], checked: boolean) => {
      if (checked) {
        onChange([
          ...planningClassifications,
          ...groupPlanningClassifications.filter(
            planningClassification =>
              !selectedPlanningClassifications.has(planningClassification),
          ),
        ])
        return
      }
      const groupPlanningClassificationsSet = new Set(groupPlanningClassifications)
      onChange(
        planningClassifications.filter(
          planningClassification =>
            !groupPlanningClassificationsSet.has(planningClassification),
        ),
      )
    },
  )

  return (
    <Box
      component="ul"
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(1, minmax(0, 1fr))',
          lg: 'repeat(2, minmax(0, 1fr))',
        },
        gap: 2,
        padding: 2,
        margin: 0,
        listStyle: 'none',
      }}
    >
      {groups.map(group => (
        <ClassificationGroup
          key={group.id}
          title={group.title}
          searchPlaceholder={group.searchPlaceholder}
          options={group.options}
          selectedPlanningClassifications={selectedPlanningClassifications}
          onChange={onClassificationChange}
          onSelectAllChange={onSelectAllChange}
          onBlur={onBlur}
        />
      ))}
    </Box>
  )
}

export default memo(ClassificationGrid)
